import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdCheck } from 'react-icons/md';
import '../master/MasterData.css';

interface JournalLine {
  accountCode: string;
  accountName: string;
  description: string;
  debit: number;
  credit: number;
}

interface JournalEntry {
  entryId: number;
  entryNo: string;
  entryDate: string;
  entryType: 'MANUAL' | 'DEPRECIATION' | 'ACCRUAL' | 'ADJUSTMENT' | 'REVERSAL';
  referenceNo: string;
  description: string;
  totalDebit: number;
  totalCredit: number;
  status: 'DRAFT' | 'POSTED' | 'REVERSED';
  lines: JournalLine[];
  createdBy: string;
  postedBy: string;
  postedDate: string;
  remarks: string;
}

const JournalEntry: React.FC = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch journal entries from API
    setEntries([
      {
        entryId: 1,
        entryNo: 'JE-2025-0001',
        entryDate: '2025-02-28',
        entryType: 'DEPRECIATION',
        referenceNo: 'DEP-2025-02',
        description: 'Monthly depreciation expense - February 2025',
        totalDebit: 45000,
        totalCredit: 45000,
        status: 'POSTED',
        lines: [
          {
            accountCode: '5200',
            accountName: 'Depreciation Expense',
            description: 'Monthly depreciation',
            debit: 45000,
            credit: 0
          },
          {
            accountCode: '1510',
            accountName: 'Accumulated Depreciation - Equipment',
            description: 'Monthly depreciation',
            debit: 0,
            credit: 45000
          }
        ],
        createdBy: 'Accounting Manager',
        postedBy: 'Accounting Manager',
        postedDate: '2025-02-28',
        remarks: 'Automated monthly entry'
      },
      {
        entryId: 2,
        entryNo: 'JE-2025-0002',
        entryDate: '2025-03-01',
        entryType: 'ACCRUAL',
        referenceNo: 'ACR-2025-001',
        description: 'Accrued utilities expense for February',
        totalDebit: 25000,
        totalCredit: 25000,
        status: 'POSTED',
        lines: [
          {
            accountCode: '5300',
            accountName: 'Utilities Expense',
            description: 'Accrued utilities',
            debit: 25000,
            credit: 0
          },
          {
            accountCode: '2100',
            accountName: 'Accrued Expenses',
            description: 'Accrued utilities',
            debit: 0,
            credit: 25000
          }
        ],
        createdBy: 'Accounting Staff',
        postedBy: 'Accounting Manager',
        postedDate: '2025-03-01',
        remarks: 'Month-end accrual'
      },
      {
        entryId: 3,
        entryNo: 'JE-2025-0003',
        entryDate: '2025-03-05',
        entryType: 'ADJUSTMENT',
        referenceNo: 'ADJ-2025-001',
        description: 'Correction of inventory valuation error',
        totalDebit: 15000,
        totalCredit: 15000,
        status: 'DRAFT',
        lines: [
          {
            accountCode: '1300',
            accountName: 'Inventory',
            description: 'Inventory adjustment',
            debit: 15000,
            credit: 0
          },
          {
            accountCode: '4100',
            accountName: 'Cost of Goods Sold',
            description: 'COGS adjustment',
            debit: 0,
            credit: 15000
          }
        ],
        createdBy: 'Accounting Staff',
        postedBy: '',
        postedDate: '',
        remarks: 'Awaiting approval'
      }
    ]);
  }, []);

  const filteredEntries = entries.filter(entry =>
    entry.entryNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.referenceNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingEntry(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setViewMode(true);
    setShowModal(true);
  };

  const handlePost = (entryId: number) => {
    if (window.confirm('Post this journal entry? This action cannot be undone.')) {
      alert('Journal entry posted');
      // TODO: Update status to POSTED
    }
  };

  const handleDelete = (entryId: number) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      setEntries(entries.filter(e => e.entryId !== entryId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'POSTED': return 'badge-success';
      case 'REVERSED': return 'badge-danger';
      default: return 'badge-warning';
    }
  };

  const getEntryTypeBadge = (type: string) => {
    switch (type) {
      case 'MANUAL': return <span className="badge badge-primary">MANUAL</span>;
      case 'DEPRECIATION': return <span className="badge badge-info">DEPRECIATION</span>;
      case 'ACCRUAL': return <span className="badge badge-warning">ACCRUAL</span>;
      case 'ADJUSTMENT': return <span className="badge badge-secondary">ADJUSTMENT</span>;
      case 'REVERSAL': return <span className="badge badge-danger">REVERSAL</span>;
      default: return <span className="badge badge-secondary">{type}</span>;
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Journal Entry</h2>
            <p className="subtitle">Manual journal entries and adjustments</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Entry
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search journal entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Entry No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Reference No</th>
                  <th>Total Debit</th>
                  <th>Total Credit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map(entry => (
                  <tr key={entry.entryId}>
                    <td className="code">{entry.entryNo}</td>
                    <td>{new Date(entry.entryDate).toLocaleDateString('en-US')}</td>
                    <td>{getEntryTypeBadge(entry.entryType)}</td>
                    <td className="description">{entry.description}</td>
                    <td>{entry.referenceNo}</td>
                    <td className="amount">₱{entry.totalDebit.toLocaleString()}</td>
                    <td className="amount">₱{entry.totalCredit.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(entry.status)}`}>
                        {entry.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(entry)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        {entry.status === 'DRAFT' && (
                          <>
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(entry)}
                              title="Edit"
                            >
                              <MdEdit />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => handlePost(entry.entryId)}
                              title="Post Entry"
                              style={{ color: '#28a745' }}
                            >
                              <MdCheck />
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(entry.entryId)}
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredEntries.length === 0 && (
              <div className="no-data">
                <p>No journal entries found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && editingEntry && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewMode ? 'View Journal Entry' : 'Edit Journal Entry'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Entry No</label>
                  <input type="text" value={editingEntry.entryNo} disabled />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="text" value={new Date(editingEntry.entryDate).toLocaleDateString('en-US')} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <input type="text" value={editingEntry.entryType} disabled />
                </div>
                <div className="form-group">
                  <label>Reference No</label>
                  <input type="text" value={editingEntry.referenceNo} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" value={editingEntry.description} disabled />
              </div>

              <h4 className="section-heading">Journal Lines</h4>
              <div className="data-table-container">
                <table className="item-table">
                  <thead>
                    <tr>
                      <th>Account Code</th>
                      <th>Account Name</th>
                      <th>Description</th>
                      <th className="amount-header">Debit</th>
                      <th className="amount-header">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editingEntry.lines.map((line, index) => (
                      <tr key={index}>
                        <td className="code">{line.accountCode}</td>
                        <td>{line.accountName}</td>
                        <td>{line.description}</td>
                        <td className="amount">
                          {line.debit > 0 ? `₱${line.debit.toLocaleString()}` : '-'}
                        </td>
                        <td className="amount">
                          {line.credit > 0 ? `₱${line.credit.toLocaleString()}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan={3} style={{ textAlign: 'right' }}>TOTAL:</td>
                      <td className="amount">₱{editingEntry.totalDebit.toLocaleString()}</td>
                      <td className="amount">₱{editingEntry.totalCredit.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        Balance:
                      </td>
                      <td colSpan={2} style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: editingEntry.totalDebit === editingEntry.totalCredit ? '#28a745' : '#dc3545'
                      }}>
                        {editingEntry.totalDebit === editingEntry.totalCredit ? '✓ BALANCED' : '✗ NOT BALANCED'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Remarks</label>
                <textarea value={editingEntry.remarks} disabled />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default JournalEntry;
